/**
 * Nion Lights — <glow-belt> Web Component
 *
 * Usage:
 *   <glow-belt preset="rainbow-flow" led-count="56" perspective></glow-belt>
 *
 * Attributes (all observed — attributeChangedCallback fires for changes):
 *   preset      string   ID from PRESETS array. Default: 'rainbow-flow'
 *   led-count   number   Number of LEDs. Default: 56
 *   perspective boolean  Presence = true. Enables rotateX(62deg) + floor reflection.
 *
 * Public API:
 *   el.setPreset(idOrIndex)  Switch preset. String = ID lookup; Number = index.
 *   el.play()                Resume RAF if paused.
 *   el.pause()               Freeze canvas at last frame.
 *   el.nextPreset()          Advance to next preset (wraps).
 *
 * Events:
 *   preset-change  CustomEvent  { id, index, preset }  bubbles: true
 *
 * Light DOM (no shadow root) — CSS custom properties from nion-tokens.css are
 * accessible without ::part() or variable forwarding. The <canvas> is the only child.
 */

import { PRESETS, PRESET_MAP } from 'glow-belt-presets';

class GlowBelt extends HTMLElement {
  static get observedAttributes() {
    // Without this, attributeChangedCallback is NEVER invoked by the browser.
    return ['preset', 'led-count', 'perspective'];
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    // Canvas — light DOM, no shadow root
    this._canvas = document.createElement('canvas');
    // Explicit CSS size prevents the canvas intrinsic size from affecting parent
    // layout and triggering a ResizeObserver feedback loop that blows up dimensions.
    // min-width/max-width:0/100% stops the canvas's HTML width attribute from
    // becoming the min-content of grid/flex parents, which would force overflow.
    this._canvas.style.cssText = 'display:block;width:100%;height:100%;min-width:0;max-width:100%;';
    this.appendChild(this._canvas);
    this._ctx = this._canvas.getContext('2d');

    // Host sizing — CSS custom property inheritance works because we're in light DOM
    this.style.cssText = 'display:block;width:100%;aspect-ratio:1.4/1;min-width:0;';

    // Initial state from attributes
    this._ledCount   = parseInt(this.getAttribute('led-count')) || 56;
    this._perspective = this.hasAttribute('perspective');
    this._raf        = null;
    this._playing    = false;
    this._startTime  = performance.now();
    this._resizeTimer = null;

    // Set initial preset (setPreset also initializes _presetIndex)
    const presetAttr = this.getAttribute('preset') || 'rainbow-flow';
    this._setPresetInternal(presetAttr, /* dispatch */ false);

    // ResizeObserver — debounced 100ms to avoid thrashing during window resize
    this._ro = new ResizeObserver(() => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._resize(), 100);
    });
    this._ro.observe(this);

    // IntersectionObserver — pause RAF when < 10% visible, resume when back
    this._io = new IntersectionObserver(([entry]) => {
      entry.isIntersecting ? this.play() : this.pause();
    }, { threshold: 0.1 });
    this._io.observe(this);

    // Shopify Theme Editor: IntersectionObserver may never fire in the editor
    // iframe because the element is off-screen in the parent document's viewport.
    // shopify:section:load fires when a section is shown in the editor — force play.
    this._onSectionLoad = () => this.play();
    document.addEventListener('shopify:section:load', this._onSectionLoad);

    // Initial render
    this._resize();
    this.play();
  }

  disconnectedCallback() {
    this.pause();
    clearTimeout(this._resizeTimer);
    if (this._ro)  { this._ro.disconnect();  this._ro  = null; }
    if (this._io)  { this._io.disconnect();  this._io  = null; }
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
  }

  attributeChangedCallback(name, _, newVal) {
    switch (name) {
      case 'preset':
        this._setPresetInternal(newVal, /* dispatch */ true);
        break;
      case 'led-count':
        this._ledCount = parseInt(newVal) || 56;
        this._resize();
        break;
      case 'perspective':
        this._perspective = this.hasAttribute('perspective');
        this._resize();
        break;
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Switch preset without restarting RAF or resetting animation time.
   * @param {string|number} idOrIndex  Preset ID string or PRESETS array index.
   */
  setPreset(idOrIndex) {
    this._setPresetInternal(idOrIndex, /* dispatch */ true);
  }

  /** Resume animation. No-op if already playing. */
  play() {
    if (this._playing) return;
    this._playing = true;
    this._scheduleFrame();
  }

  /** Freeze canvas at last rendered frame. */
  pause() {
    this._playing = false;
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
  }

  /** Advance to the next preset (wraps around). */
  nextPreset() {
    this._presetIndex = (this._presetIndex + 1) % PRESETS.length;
    this._setPresetInternal(this._presetIndex, /* dispatch */ true);
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  _setPresetInternal(idOrIndex, dispatch) {
    let preset, index;

    if (typeof idOrIndex === 'number') {
      // Correct modular wrap: handles negative indices gracefully
      index  = ((Math.trunc(idOrIndex) % PRESETS.length) + PRESETS.length) % PRESETS.length;
      preset = PRESETS[index];
    } else {
      preset = PRESET_MAP.get(idOrIndex);
      if (!preset) {
        console.warn(`[glow-belt] Unknown preset id: "${idOrIndex}". Falling back to rainbow-flow.`);
        preset = PRESETS[0];
      }
      index = PRESETS.indexOf(preset);
    }

    this._preset      = preset;
    this._presetIndex = index;

    if (dispatch) {
      this.dispatchEvent(new CustomEvent('preset-change', {
        bubbles: true,
        detail: { id: preset.id, index, preset },
      }));
    }
  }

  _resize() {
    if (!this._canvas) return;
    const dpr  = window.devicePixelRatio || 1;
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    this._canvas.width  = rect.width  * dpr;
    this._canvas.height = rect.height * dpr;

    // setTransform REPLACES the matrix — safe to call on every resize.
    // Do NOT use ctx.scale(dpr, dpr) — scale() multiplies the existing matrix,
    // so calling it on each resize would double-scale and corrupt rendering.
    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this._w = rect.width;
    this._h = rect.height;
  }

  _scheduleFrame() {
    if (!this._playing) return;
    this._raf = requestAnimationFrame(() => {
      this._draw();
      this._scheduleFrame();
    });
  }

  _draw() {
    const ctx     = this._ctx;
    const w       = this._w;
    const h       = this._h;
    const n       = this._ledCount;
    const preset  = this._preset;
    const t       = (performance.now() - this._startTime) / 1000;

    if (!ctx || !w || !h) return;

    // ── Ellipse geometry ──────────────────────────────────────────────────
    const cx      = w / 2;
    const yOffset = this._perspective ? h * 0.05 : 0;
    const cy      = h / 2 + yOffset;
    const rx      = w * 0.38;
    const ry      = this._perspective ? h * 0.13 : h * 0.34;

    // 1. Clear
    ctx.clearRect(0, 0, w, h);

    // 2. Belt strap
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.strokeStyle  = 'rgba(255,255,255,0.08)';
    ctx.lineWidth    = 22;
    ctx.shadowBlur   = 12;
    ctx.shadowColor  = 'rgba(255,255,255,0.05)';
    ctx.stroke();
    ctx.shadowBlur   = 0;

    // 3. LEDs
    for (let i = 0; i < n; i++) {
      const angle  = (i / n) * Math.PI * 2 - Math.PI / 2;
      const lx     = cx + rx * Math.cos(angle);
      const ly     = cy + ry * Math.sin(angle);
      const { h: hue, s, l, intensity } = preset.fn(i, n, t);
      const dimmedL = l * intensity;
      const color   = `hsl(${hue % 360}, ${s}%, ${dimmedL}%)`;

      // Halo glow (skip very-dim LEDs to save fillStyle + arc overhead)
      if (intensity > 0.05) {
        const grad = ctx.createRadialGradient(lx, ly, 0, lx, ly, 18 * intensity);
        grad.addColorStop(0, color);
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(lx, ly, 18 * intensity, 0, Math.PI * 2);
        ctx.fill();
      }

      // LED core
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(lx, ly, 2.5, 0, Math.PI * 2);
      ctx.fill();

      // Specular highlight — offset slightly top-left per the design handoff
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.beginPath();
      ctx.arc(lx - 0.6, ly - 0.6, 0.9, 0, Math.PI * 2);
      ctx.fill();
    }

    // 4. Floor reflection (perspective mode, bottom arc only)
    if (this._perspective) {
      ctx.save();
      ctx.globalAlpha = 0.18;

      // Translate origin to just below the belt's bottom arc.
      // scale(1, -0.5) flips vertically and compresses to 50% height.
      ctx.translate(cx, cy + yOffset + ry * 0.6);
      ctx.scale(1, -0.5);

      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const lx    = cx + rx * Math.cos(angle);
        const ly    = cy + ry * Math.sin(angle);

        // Only reflect bottom-arc LEDs (those below the belt center)
        if (ly <= cy) continue;

        // Coords relative to the translated origin (cx, cy+yOffset+ry*0.6).
        // reflLy = ry * sin(angle) intentionally does NOT subtract the ry*0.6
        // component baked into the translate origin. After scale(1,-0.5) this
        // places the reflection upward from the origin, i.e., just below the belt.
        const reflLx = rx * Math.cos(angle);
        const reflLy = ry * Math.sin(angle);

        const { h: hue, s, l, intensity } = preset.fn(i, n, t);
        const dimmedL = l * intensity;
        const color   = `hsl(${hue % 360}, ${s}%, ${dimmedL}%)`;

        if (intensity > 0.05) {
          const grad = ctx.createRadialGradient(reflLx, reflLy, 0, reflLx, reflLy, 18 * intensity);
          grad.addColorStop(0, color);
          grad.addColorStop(1, 'transparent');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(reflLx, reflLy, 18 * intensity, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(reflLx, reflLy, 2.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.beginPath();
        ctx.arc(reflLx - 0.6, reflLy - 0.6, 0.9, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }
  }
}

customElements.define('glow-belt', GlowBelt);
